/**
 * Lista de botões de ação do vídeo (até 5): criação, rótulos e a regra de "aparece agora?".
 * A regra de tempo é a mesma do player (e-player-js/js/action-buttons-core.js): término vazio
 * = até o fim do vídeo.
 */
import type { ActionButton } from '@/store/videoFeatures/types/overlay-features';
import { defaultTemplate } from './presets';

export const MAX_ACTION_BUTTONS = 5;

const MODE_LABEL: Record<NonNullable<ActionButton['buttonType']>, string> = {
  'abaixo-video': 'Abaixo do vídeo',
  'sobre-video': 'Sobre o vídeo',
  html: 'HTML',
};

/** `ab_` + 8 hex: o formato que o backend aceita e o player usa no DOM. */
export function newActionButtonId(): string {
  let hex = '';
  while (hex.length < 8) hex += Math.floor(Math.random() * 16).toString(16);
  return `ab_${hex}`;
}

/** Botão novo: o Padrão de sempre, abaixo do vídeo, o vídeo todo. */
export function createActionButton(): ActionButton {
  return {
    id: newActionButtonId(),
    enabled: true,
    buttonType: 'abaixo-video',
    text: 'QUERO CONTINUAR',
    url: '',
    size: 'medium',
    position: 'center',
    horizontalOffset: 0,
    verticalOffset: 0,
    timing: { showThroughout: true },
    style: {
      backgroundColor: '#1D4FFF',
      textColor: '#FFFFFF',
      hoverBackgroundColor: '#1A47E5',
      hoverTextColor: '#FFFFFF',
      borderRadius: 8,
      shadow: true,
      pulse: false,
    },
    template: defaultTemplate(),
  };
}

export function actionButtonLabel(index: number): string {
  return `Botão ${index + 1}`;
}

/**
 * "MM:SS" → segundos; null para vazio ou fora do formato. Segundos com dois dígitos: o campo de
 * tempo grava o que foi digitado, e "01:7" é tempo incompleto, não 67s.
 */
export function mmssToSeconds(value: string | null | undefined): number | null {
  if (typeof value !== 'string') return null;
  const match = /^\s*(\d+):(\d{2})\s*$/.exec(value);
  if (!match) return null;
  const seconds = Number(match[2]);
  return seconds > 59 ? null : Number(match[1]) * 60 + seconds;
}

/** 4510 → "75:10". */
export function secondsToMMSS(total: number): string {
  const s = Math.max(0, Math.floor(total));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Ex.: "Sobre o vídeo • 01:20 – 02:00", "Abaixo do vídeo • o vídeo todo". */
export function actionButtonSummary(button: ActionButton): string {
  const mode = MODE_LABEL[button.buttonType || 'abaixo-video'] ?? MODE_LABEL['abaixo-video'];
  const timing = button.timing;
  if (!timing || timing.showThroughout || (!timing.start && !timing.end)) {
    return `${mode} • o vídeo todo`;
  }
  return `${mode} • ${timing.start || '00:00'} – ${timing.end || 'fim'}`;
}

export function isActionButtonVisible(button: ActionButton, currentTime: number): boolean {
  const timing = button.timing;
  if (!timing || timing.showThroughout) return true;
  const start = mmssToSeconds(timing.start) ?? 0;
  const end = mmssToSeconds(timing.end);
  return currentTime >= start && (end === null || currentTime <= end);
}

/**
 * Botões ligados que a prévia mostra agora. O aberto no editor (`forcedId`) aparece sempre,
 * para o usuário ver o que está editando.
 */
export function selectVisibleActionButtons(
  buttons: ActionButton[] | undefined,
  currentTime: number,
  forcedId: string | null,
): ActionButton[] {
  return (buttons ?? []).filter(
    (button) => button.enabled === true && (button.id === forcedId || isActionButtonVisible(button, currentTime)),
  );
}
